import React, { useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { useAuth } from '../context/AuthContext'

const Wrapper = styled.div`
  min-height: 100vh;
  display: grid;
  place-items: center;
  padding: 1rem;
  background: linear-gradient(130deg, #0f172a 0%, #1d4ed8 60%, #16a34a 100%);
`

const Card = styled.section`
  width: min(420px, 100%);
  background: rgba(248, 250, 252, 0.98);
  border-radius: 16px;
  padding: 1.3rem;
  box-shadow: 0 24px 50px rgba(15, 23, 42, 0.32);

  h1 {
    margin: 0;
    font-family: 'Playfair Display', serif;
    color: #0f172a;
  }

  p {
    margin: 0.65rem 0 1.2rem;
    color: #475569;
    line-height: 1.6;
    font-family: 'Lato', sans-serif;
  }
`

const Label = styled.label`
  display: block;
  margin-bottom: 0.35rem;
  font-size: 0.9rem;
  color: #334155;
  font-weight: 700;
`

const Input = styled.input`
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 0.75rem 0.8rem;
  margin-bottom: 0.9rem;
  font-size: 1rem;
`

const ErrorBox = styled.div`
  background: #fee2e2;
  border: 1px solid #fca5a5;
  color: #991b1b;
  padding: 0.65rem 0.75rem;
  border-radius: 8px;
  margin-bottom: 0.9rem;
`

const SubmitButton = styled.button`
  width: 100%;
  border: none;
  border-radius: 999px;
  padding: 0.8rem 1rem;
  background: #111827;
  color: #fff;
  cursor: pointer;
  font-weight: 700;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

function AdminLoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated, isLoading, login } = useAuth()

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')

  if (!isLoading && isAuthenticated) {
    return <Navigate to="/admin/gallery" replace />
  }

  const submit = async event => {
    event.preventDefault()
    setError('')
    setIsSubmitting(true)

    try {
      await login(username.trim(), password)
      const nextPath = location.state?.from || '/admin/gallery'
      navigate(nextPath, { replace: true })
    } catch (requestError) {
      setError(requestError.message || 'Login failed')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Wrapper>
      <Card>
        <h1>Admin Login</h1>
        <p>Sign in with your admin account to manage gallery, projects, and users.</p>

        <form onSubmit={submit}>
          {error ? <ErrorBox>{error}</ErrorBox> : null}

          <Label htmlFor="username">Username</Label>
          <Input
            id="username"
            name="username"
            value={username}
            onChange={event => setUsername(event.target.value)}
            autoComplete="username"
            required
          />

          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            name="password"
            type="password"
            value={password}
            onChange={event => setPassword(event.target.value)}
            autoComplete="current-password"
            required
          />

          <SubmitButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Signing in...' : 'Sign in'}
          </SubmitButton>
        </form>
      </Card>
    </Wrapper>
  )
}

export default AdminLoginPage
